import { Permission } from '@Constants/Permission.g';
import { useHasPermission } from '@Contexts/appContext';
import { useFeatureFlags } from '@Contexts/featureFlags';
import { useRemoteCacheInvalidation } from '@Hooks/useRemoteCacheInvalidation';
import {
    type ApiContainerAction,
    type ApiContainerStockEntry,
    type ApiPlannedContainerAction,
    EnumPlannedActionSource,
    isBooked,
} from '@Model';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { type ContainerHistoryEntry } from './containerHistory';
import { useContainerHistory } from './useContainerHistory';
import { useContainerStock } from './useContainerStock';
import { usePlannedAtSite } from './usePlannedAtSite';

export interface ContainersAtSiteRequest {
    siteId: string | undefined;
    customerId: string | undefined;
    containerActions: ApiContainerAction[];
    savedAt: string;
    enabled: boolean;
    completedAt?: string;
    orderNumber: string;
}

export interface ContainersAtSite {
    standing: ApiContainerStockEntry[];
    afterSave: boolean;
    planned: ApiPlannedContainerAction[];
    plannedFailed: boolean;
    stockFailure: string;
    history: ContainerHistoryEntry[];
    historyFailed: boolean;
    /** Also true when nothing may be read. */
    isEmpty: boolean;
    /** Whether stock and planned placements were read, so a container missing from both is really not there. */
    stockKnown: boolean;
}

const SITE_QUERIES = ['container-stock', 'planned-at-site', 'container-history'];

/** What stands at a customer's site, what open orders plan there and what happened there. */
// @ai-generated 2026-10-14T14:26:40+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5
export function useContainersAtSite({
    siteId,
    customerId,
    containerActions,
    savedAt,
    enabled,
    completedAt,
    orderNumber,
}: ContainersAtSiteRequest): ContainersAtSite {
    const { isFeatureEnabled } = useFeatureFlags();
    const hasPermission = useHasPermission();
    const queryClient = useQueryClient();

    const readSiteAgain = useCallback(() => {
        SITE_QUERIES.forEach((key) => void queryClient.invalidateQueries({ queryKey: [key] }));
    }, [queryClient]);
    useRemoteCacheInvalidation('container_actions', readSiteAgain);

    const canBeRead =
        enabled &&
        isFeatureEnabled('CONTAINER_SERVICE') &&
        hasPermission(Permission.AuftragsbearbeitungContainerStockRead) &&
        !!siteId &&
        !!customerId;
    const readSiteId = canBeRead ? siteId : undefined;
    const readCustomerId = canBeRead ? customerId : undefined;
    const bookedContainers = containerActions.filter((action) => isBooked(action.booking_status)).length;

    const stock = useContainerStock({
        siteId: readSiteId,
        customerId: readCustomerId,
        bookedContainers,
        savedAt,
    });
    const planned = usePlannedAtSite({ siteId: readSiteId, customerId: readCustomerId, savedAt });
    const history = useContainerHistory({
        siteId: readSiteId,
        customerId: readCustomerId,
        bookedContainers,
        savedAt,
    });

    const hasAnything =
        containerActions.length > 0 ||
        stock.entries.length > 0 ||
        planned.entries.length > 0 ||
        history.entries.length > 0 ||
        !!stock.failure ||
        planned.failed ||
        history.failed;

    const afterSave = !!completedAt && containerActions.some((action) => !isBooked(action.booking_status));
    return {
        standing: stock.entries,
        afterSave,
        planned: planned.entries.filter(
            (action) =>
                !orderNumber || action.source !== EnumPlannedActionSource.Order || action.source_number !== orderNumber,
        ),
        plannedFailed: planned.failed,
        stockFailure: stock.failure,
        history: history.entries,
        historyFailed: history.failed,
        isEmpty: !canBeRead || !hasAnything,
        stockKnown: canBeRead && !stock.isLoading && !stock.failure && planned.loaded && !planned.failed,
    };
}
